import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Title} from '@angular/platform-browser';
import {AuthService} from './auth/auth.service';
import {TranslateService, LangChangeEvent} from '@ngx-translate/core';
import {of, Subscription} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TitleService implements OnDestroy {

  /**
   * Default translation key used if no valid realm-title mapping is found.
   */
  private readonly defaultKey: string = 'URUK.TITLE.DEFAULT';

  /**
   * Stores the last used title translation key
   */
  private currentKey: string = this.defaultKey;

  /**
   * Subscription for language change event
   */
  private readonly langSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private http: HttpClient,
    private title: Title
  ) {
    // Listen for language change and re-translate the title
    this.langSubscription = this.translateService.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.updateTitleWithCurrentKey();
      });
  }

  /**
   * Fetches the realm-specific title from theme.json and applies the translated title.
   * Falls back to shared theme.json or default key if request fails.
   */
  setDynamicTitle(): void {
    const realmName = this.authService.getRealmId() || '';
    const jsonUrl = `https://assets.geleceginsehri.com/${realmName}/theme.json`;
    const fallbackUrl = 'https://assets.geleceginsehri.com/documents/theme.json';

    this.http.get<{ defaultTitle: string }>(jsonUrl).pipe(
      catchError(() => this.http.get<{ defaultTitle: string }>(fallbackUrl)),
      catchError(() => of({ defaultTitle: this.defaultKey })),
      switchMap(response => {
        this.currentKey = response.defaultTitle || this.defaultKey;
        return this.translateService.get(this.currentKey);
      })
    ).subscribe(translatedTitle => {
      this.title.setTitle(translatedTitle);
    });
  }

  /**
   * Applies translation for the current key when language changes
   */
  private updateTitleWithCurrentKey(): void {
    const key = this.currentKey || this.defaultKey;
    this.translateService.get(key).subscribe(translated => {
      this.title.setTitle(translated);
    });
  }

  /**
   * Unsubscribes from language change events to avoid memory leaks
   */
  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
